export class RuntimeClock {
  private timer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private speed = 1;
  private ticking = false;
  private running = false;
  private disposed = false;

  constructor(
    private readonly tick: () => void | Promise<void>,
    private readonly baseIntervalMs = 500,
    private readonly onError?: (error: unknown) => void,
    private readonly nextIntervalMs?: () => number,
  ) {}

  start(): void {
    if (this.disposed)
      throw new Error('运行时钟已经释放');
    this.stop();
    this.running = true;
    this.scheduleNext();
  }

  stop(): void {
    if (this.timer !== null)
      globalThis.clearTimeout(this.timer);
    this.timer = null;
    this.running = false;
  }

  setSpeed(speed: number): void {
    if (!Number.isFinite(speed) || speed <= 0)
      throw new Error('时钟速度必须大于 0');
    this.speed = speed;
    if (this.running && !this.ticking) {
      if (this.timer !== null)
        globalThis.clearTimeout(this.timer);
      this.timer = null;
      this.scheduleNext();
    }
  }

  dispose(): void {
    this.stop();
    this.disposed = true;
  }

  private async runTick(): Promise<void> {
    this.timer = null;
    if (this.ticking || this.disposed || !this.running)
      return;
    this.ticking = true;
    try {
      await this.tick();
    }
    catch (error) {
      this.stop();
      this.onError?.(error);
    }
    finally {
      this.ticking = false;
      if (this.running)
        this.scheduleNext();
    }
  }

  private scheduleNext(): void {
    if (!this.running || this.disposed || this.timer !== null)
      return;
    const requestedInterval = this.nextIntervalMs?.() ?? this.baseIntervalMs;
    const interval = Number.isFinite(requestedInterval)
      ? Math.max(0, requestedInterval)
      : this.baseIntervalMs;
    this.timer = globalThis.setTimeout(
      () => void this.runTick(),
      Math.max(16, interval / this.speed),
    );
  }
}
